import { apiV1, apiV2 } from "@/store";
import router from "@/router";

const state = {
  call_id: -999,
  converted_location_code: "",
  disposition_id: "",
  recording: true,

  call_converted: false,
  accounts_receivable: false,
  phone_lead: false,
  phone_reservation: false,
  phone_rental: false,

  // The following is only for reservations or rentals
  reservation_id: "",
  unit_size: "",
  unit_type: "",
  quoted_price: "",
  promotion_text: "",

  first_name: "",
  last_name: "",
  email: "",
  street_address: "",
  city: "",
  state: "",
  postal_code: "",

  video: false,
  video_room_name: "",
  video_customer: ""
};

const actions = {
  getCallTypes: ({ }) => {
    return new Promise((resolve, reject) => {
      apiV1.get(`call-types/for-agents`)
        .then((resp) => {
          resolve(resp);
        })
        .catch((err) => {
          reject(err);
        });
    });
  },

  beginCall: ({ commit, dispatch }, payload) => {
    return new Promise((resolve, reject) => {
      apiV2.post(`calls`, payload)
        .then((resp) => {
          if (!resp.data) {
            throw new Error("data unavailable");
          }

          if (payload.location_code !== "N001" && payload.location_code !== "CC00") {
            dispatch("getFacility", payload.location_code)
              .then((facilityResp) => {
                localStorage.setItem("facilityId", facilityResp.data.facility.id);
                resolve({ ...resp, facilityId: facilityResp.data.facility.id });
              })
              .catch((facilityErr) => {
                localStorage.removeItem("callStarted");
                localStorage.removeItem("customerSource");
                dispatch("handleError", facilityErr);
                reject(facilityErr);
              });
          } else {
            resolve(resp);
          }

          commit("callStarted");
          localStorage.setItem("callStarted", true);
          localStorage.setItem("customerSource", resp.data.tracking.source);
        })
        .catch((err) => {
          localStorage.removeItem("callStarted");
          localStorage.removeItem("customerSource");
          dispatch("handleError", err);
          reject(err);
        });
    });
  },

  routeCall: ({ commit, dispatch }, payload) => {
    if (payload.call_type === "lead") {
      localStorage.setItem("reservationId", payload.contact_id);
    } else if (payload.locationCode !== "N001") {
      localStorage.setItem("locationCode", payload.location_code);
    }
    localStorage.setItem("callId", payload.callId);
    localStorage.setItem("ctmId", payload.ctm_id);

    const shouldGoFacilityDetails = (
      payload.call_type === "lead" ||
      payload.call_type === "incoming" ||
      (payload.call_type === "followup" && !payload.tenant_id)
    );
    if (payload.locationCode === "N001") { // <--- Incoming call from the 1-800 number
      router.push({ name: "search" }).catch(() => { });
    } else if (shouldGoFacilityDetails) { // <--- Sales call; user unknown
      router.push({
        name: "facilityDetails",
        params: { facilityId: payload.router.facilityId }
      }).catch(() => { });
    } else if (payload.call_type === "followup") { // <--- Sales call; user known
      router.push({
        name: "tenantDetails",
        params: {
          facilityId: payload.router.facilityId,
          tenantId: payload.tenant_id
        }
      }).catch(() => { });
    } else if (payload.call_type === "accounts-receivable") { // <--- A/R call; user known
      router.push({
        name: "arTenant",
        params: {
          facilityId: payload.router.facilityId,
          tenantId: payload.router.tenantId
        }
      }).catch(() => { });
    } else {
      router.push({ name: "arTenantSearch" }).catch(() => { }); // <--- A/R call; user unknown
    }
  },

  answerVideoCall: ({ commit, dispatch }, payload) => {
    return new Promise((resolve, reject) => {
      apiV1.post(`video-calls/answer`, payload)
        .then((resp) => {
          localStorage.setItem("callId", resp.data.call.id);
          commit("callStarted");
          localStorage.setItem("callStarted", true);
          localStorage.setItem("customerSource", "Kiosk Video Call");
          dispatch("getFacility", payload.location_code)
            .then((resp) => {
              let facilityId = resp.data.facility.id;
              localStorage.setItem("facilityId", facilityId);
              router.push({
                name: "details",
                params: { facilityId: facilityId }
              }).catch(() => { });
              resolve(resp);
            })
            .catch((err) => {
              dispatch("handleError", err);
              reject(err);
            });
        })
        .catch((err) => {
          dispatch("handleError", err);
          reject(err);
        });
    });
  },

  endCall: ({ commit }, payload) => {
    commit("callEnded");
    return new Promise((resolve, reject) => {
      apiV1.patch(`calls/${payload.call_id}/disposition`, payload)
        .then((resp) => {
          resolve(resp);
        })
        .catch((err) => {
          reject(err);
        });
    });
  },

  startRecording: ({ commit }) => {
    let ctmId = localStorage.getItem("ctmId");
    return new Promise((resolve, reject) => {
      apiV1.post(`ctm/calls/${ctmId}/start-recording`)
        .then((resp) => {
          commit("recordingStarted");
          resolve(resp);
        })
        .catch((err) => {
          reject(err);
        });
    });
  },

  stopRecording: ({ commit }) => {
    const ctmId = localStorage.getItem("ctmId");
    return new Promise((resolve, reject) => {
      apiV1.post(`ctm/calls/${ctmId}/stop-recording`)
        .then((resp) => {
          commit("recordingStopped");
          resolve(resp);
        })
        .catch((err) => {
          reject(err);
        });
    });
  },

  sendText: ({ }, payload) => {
    return new Promise((resolve, reject) => {
      apiV1.post(`ctm/sms`, payload)
        .then((resp) => {
          resolve(resp);
        })
        .catch((err) => {
          reject(err);
        });
    });
  },

  sendReservationConfirmationEmail: ({ }, payload) => {
    return new Promise((resolve, reject) => {
      apiV2.post(`facilities/${payload.facility_id}/reservations/${payload.external_reservation_id}/email-confirmation`, payload)
        .then((resp) => {
          resolve(resp);
        })
        .catch((err) => {
          reject(err);
        });
    });
  },

  sendExpressCheckInEmail: ({ }, payload) => {
    return new Promise((resolve, reject) => {
      apiV2.post(`facilities/${payload.facility_id}/reservations/${payload.external_id}/email-check-in`, payload)
        .then((resp) => {
          resolve(resp);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }
};

const mutations = {
  callStarted: (state) => {
    state.phoneActive = true;
  },

  callEnded: (state) => {
    state.phoneActive = false;

    state.call_id = -999;
    state.converted_location_code = "";
    state.disposition_id = "";

    state.accounts_receivable = false;
    state.call_converted = false;
    state.phone_lead = false;
    state.phone_reservation = false;
    state.phone_rental = false;

    state.reservation_id = "";
    state.unit_size = "";
    state.unit_type = "";
    state.quoted_price = "";
    state.promotion_text = "";

    state.first_name = "";
    state.last_name = "";
    state.email = "";
    state.street_address = "";
    state.city = "";
    state.state = "";
    state.postal_code = "";

    state.video = false;
  },

  setArData: (state) => {
    state.accounts_receivable = true;
  },

  setReservationData: (state, payload) => {
    state.phone_reservation = payload.reservation_type === "reservation";
    state.phone_lead = payload.reservation_type === "inquiry";
    state.call_converted = true;

    state.unit_size = payload.unit_dimensions;
    state.unit_type = payload.unit_type;
    state.quoted_price = payload.quoted_price;
    state.promotion_text = payload.promotion_text;

    state.first_name = payload.first_name;
    state.last_name = payload.last_name;
    state.email = payload.email;
  },

  setRentalData: (state, payload) => {
    state.phone_rental = true;
    state.call_converted = true;

    state.unit_size = payload.unit_size;
    state.unit_type = payload.unit_type;
    state.quoted_price = payload.quoted_price;
    state.promotion_text = payload.promotion_text;

    state.first_name = payload.first_name;
    state.last_name = payload.last_name;
    state.email = payload.email;
    state.street_address = payload.billing_address;
    state.city = payload.mailing_city;
    state.state = payload.mailing_state;
    state.postal_code = payload.mailing_postal_code;
  },

  setReservationId: (state, reservationId) => {
    state.reservation_id = reservationId;
  },

  recordingStarted: (state) => {
    state.recording = true;
  },

  recordingStopped: (state) => {
    state.recording = false;
  },

  setVideoCall: (state) => {
    state.video = true;
  },

  setVideoRoomName: (state, roomName) => {
    state.video_room_name = roomName;
  },

  setVideoCustomer: (state, videoCustomer) => {
    state.video_customer = videoCustomer;
  }
};

export default {
  state,
  actions,
  mutations
};
